/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, MessageCircle } from 'lucide-react';
import { getAllPosts } from '../utils/blogLoader';

const SITE_URL = 'https://buyerview.ai';
const SITE_NAME = 'BuyerView';

export function About() {
  const allPosts = getAllPosts();
  const kumarilPosts = allPosts.filter(p =>
    typeof p.author === 'string' && p.author.toLowerCase().includes('kumaril')
  );

  // JSON-LD Person referenced from every BlogPosting Kumaril writes.
  const personJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${SITE_URL}/#kumaril`,
    name: 'Kumaril',
    url: `${SITE_URL}/about`,
    jobTitle: 'Founder',
    worksFor: { '@type': 'Organization', '@id': `${SITE_URL}/#org`, name: SITE_NAME, url: SITE_URL },
    description:
      'Founder of BuyerView.ai. Writes about Australian property policy, market data, and the questions buyers should ask before they sign.',
    knowsAbout: [
      'Australian property market',
      'Negative gearing',
      'Capital gains tax',
      'First home buyers',
      'Property due diligence',
    ],
  };

  return (
    <div className="container mx-auto px-4 py-10 sm:py-14">
      <Helmet>
        <html lang="en-AU" />
        <title>{`About Kumaril · ${SITE_NAME}`}</title>
        <link rel="canonical" href={`${SITE_URL}/about`} />
        <meta
          name="description"
          content="Kumaril, founder of BuyerView. Writes about Australian property policy and the questions buyers should ask before they sign."
        />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`${SITE_URL}/about`} />
        <meta property="og:title" content={`About Kumaril · ${SITE_NAME}`} />
        <meta
          property="og:description"
          content="Founder of BuyerView. Australian property market data and policy explainers for buyers."
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`About Kumaril · ${SITE_NAME}`} />
        <script type="application/ld+json">{JSON.stringify(personJsonLd)}</script>
      </Helmet>

      <div className="max-w-3xl mx-auto">
        <header className="mb-10 pb-8 border-b border-gray-200">
          <div className="flex items-center gap-5 mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#4ECDC4] to-[#0A2342] flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
              K
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">Kumaril</h1>
              <p className="text-base text-gray-500 mt-1">Founder, BuyerView</p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed">
            BuyerView started with a simple observation: buying property in Australia is one of the
            largest financial decisions most people make, and almost every information channel
            around it is built for someone else: the agent, the developer, the investor with a
            portfolio. I built BuyerView for the person on the other side of the table.
          </p>
        </header>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What I write about</h2>
          <p className="text-base text-gray-700 leading-relaxed mb-3">
            Australian property policy, market data, and the specific questions buyers should ask
            before they sign, particularly in the gap between what agents are required to disclose
            and what an inspection actually surfaces.
          </p>
          <p className="text-base text-gray-700 leading-relaxed">
            I'm not a financial adviser or a registered tax agent. Everything I publish is general
            information drawn from primary sources (ATO, ABS, RBA, federal budget papers, CoreLogic
            data releases). When something is genuinely complex, I link to it and tell you to talk
            to your accountant.
          </p>
        </section>

        {kumarilPosts.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent posts</h2>
            <ul className="space-y-4">
              {kumarilPosts.slice(0, 5).map(post => (
                <li key={post.slug}>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block group p-4 -mx-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#4ECDC4] mb-1 leading-snug">
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p className="text-sm text-gray-600 line-clamp-2">{post.excerpt}</p>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="bg-gray-50 rounded-xl p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Get in touch</h2>
          <p className="text-base text-gray-700 leading-relaxed mb-5">
            Found a factual error in a post, or have a question you'd like to see covered? Either
            channel works.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="mailto:hello@buyerview.ai"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#4ECDC4] text-white rounded-lg hover:bg-[#3DB8B0] transition-colors text-sm font-semibold"
            >
              <Mail className="h-4 w-4" />
              hello@buyerview.ai
            </a>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg hover:border-[#4ECDC4] transition-colors text-sm font-semibold"
            >
              <MessageCircle className="h-4 w-4" />
              Read the blog
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
